import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

const COOKIE_CONSENT_KEY = 'aile_cookie_consent';

export const CookieConsent = () => {
  const { language } = useLanguage();
  const location = useLocation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  // Ne pas afficher le bandeau sur les pages admin
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    // Ne pas afficher sur les pages admin
    if (isAdminPage) {
      setShowBanner(false);
      return;
    }
    
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after a short delay for better UX
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [isAdminPage]);

  const content = {
    fr: {
      title: 'Nous utilisons des cookies',
      description: 'Ce site utilise des cookies pour améliorer votre expérience de navigation, analyser le trafic et personnaliser le contenu.',
      acceptAll: 'Tout accepter',
      rejectAll: 'Tout refuser',
      customize: 'Personnaliser',
      save: 'Enregistrer mes choix',
      moreInfo: 'En savoir plus',
      settings: {
        title: 'Paramètres des cookies',
        necessary: {
          title: 'Cookies nécessaires',
          description: 'Ces cookies sont essentiels au fonctionnement du site. Ils ne peuvent pas être désactivés.'
        },
        analytics: {
          title: 'Cookies analytiques',
          description: 'Ces cookies nous aident à comprendre comment les visiteurs utilisent le site.'
        },
        marketing: {
          title: 'Cookies marketing',
          description: 'Ces cookies sont utilisés pour vous montrer des publicités pertinentes.'
        }
      }
    },
    en: {
      title: 'We use cookies',
      description: 'This site uses cookies to improve your browsing experience, analyze traffic and personalize content.',
      acceptAll: 'Accept all',
      rejectAll: 'Reject all',
      customize: 'Customize',
      save: 'Save my choices',
      moreInfo: 'Learn more',
      settings: {
        title: 'Cookie settings',
        necessary: {
          title: 'Necessary cookies',
          description: 'These cookies are essential for the site to function. They cannot be disabled.'
        },
        analytics: {
          title: 'Analytics cookies',
          description: 'These cookies help us understand how visitors use the site.'
        },
        marketing: {
          title: 'Marketing cookies',
          description: 'These cookies are used to show you relevant advertisements.'
        }
      }
    }
  };

  const t = content[language];

  const saveConsent = (prefs) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = { necessary: true, analytics: true, marketing: true };
    setPreferences(allAccepted);
    saveConsent(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = { necessary: true, analytics: false, marketing: false };
    setPreferences(onlyNecessary);
    saveConsent(onlyNecessary);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998]"
        onClick={() => {}}
      />

      {/* Banner */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
        data-testid="cookie-consent-banner"
      >
        <div className="max-w-4xl mx-auto">
          {!showSettings ? (
            /* Main Banner */
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom duration-500">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#127abb]/10 rounded-xl flex items-center justify-center">
                    <Cookie className="text-[#127abb]" size={24} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-slate-900 mb-2">
                      {t.title}
                    </h3>
                    <p className="text-slate-600 text-sm leading-relaxed mb-4">
                      {t.description}{' '}
                      <Link to="/privacy" className="text-[#127abb] hover:underline font-medium">
                        {t.moreInfo}
                      </Link>
                    </p>
                    
                    <div className="flex flex-wrap gap-3">
                      <Button
                        onClick={handleAcceptAll}
                        className="bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full px-6"
                        data-testid="accept-all-cookies"
                      >
                        <Check size={16} className="mr-2" />
                        {t.acceptAll}
                      </Button>
                      <Button
                        onClick={handleRejectAll}
                        variant="outline"
                        className="rounded-full px-6 border-slate-300"
                        data-testid="reject-all-cookies"
                      >
                        <X size={16} className="mr-2" />
                        {t.rejectAll}
                      </Button>
                      <Button
                        onClick={() => setShowSettings(true)}
                        variant="ghost"
                        className="rounded-full px-6 text-slate-600"
                        data-testid="customize-cookies"
                      >
                        <Settings size={16} className="mr-2" />
                        {t.customize}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden animate-in slide-in-from-bottom duration-300">
              <div className="p-6 border-b border-slate-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#127abb]/10 rounded-xl flex items-center justify-center">
                      <Settings className="text-[#127abb]" size={20} />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900">
                      {t.settings.title}
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowSettings(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} className="text-slate-500" />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold text-slate-900 mb-1">
                      {t.settings.necessary.title}
                    </h4>
                    <p className="text-sm text-slate-600">
                      {t.settings.necessary.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <div className="w-12 h-6 bg-[#127abb] rounded-full flex items-center justify-end px-1 cursor-not-allowed opacity-70">
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold text-slate-900 mb-1">
                      {t.settings.analytics.title}
                    </h4>
                    <p className="text-sm text-slate-600">
                      {t.settings.analytics.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.analytics ? 'bg-[#127abb] justify-end' : 'bg-slate-300 justify-start'
                      } px-1`}
                      data-testid="toggle-analytics"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-slate-50 rounded-xl">
                  <div className="flex-1 pr-4">
                    <h4 className="font-semibold text-slate-900 mb-1">
                      {t.settings.marketing.title}
                    </h4>
                    <p className="text-sm text-slate-600">
                      {t.settings.marketing.description}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <button
                      onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                      className={`w-12 h-6 rounded-full flex items-center transition-colors ${
                        preferences.marketing ? 'bg-[#127abb] justify-end' : 'bg-slate-300 justify-start'
                      } px-1`}
                      data-testid="toggle-marketing"
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 border-t border-slate-200 flex gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="flex-1 bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full"
                  data-testid="save-cookie-preferences"
                >
                  <Check size={16} className="mr-2" />
                  {t.save}
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="rounded-full px-6"
                >
                  {t.acceptAll}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
